import { api, providesList } from "api/baseApi";
import qs from "query-string";
import { SalesOrderReportV2Response } from "types/price-list.type";
import { SalesOrderDetailV2, SalesOrderV2 } from "types/sales-order.type";

const url = "/sales-order";

export const {
	useCreateSalesOderMutation,
	useUpdateSalesOderMutation,
	useGetSalesOrdersQuery,
	useGetOneSalesOrderQuery,
	useGetSalesOrderDetailQuery,
	useDeleteSalesOrderMutation,
	useLazyGetSalesOrderInvoiceQuery,
	useCancelSalesOrderMutation,
	useCreateSalesOderV2Mutation,
	useGetOneSalesOrderV2Query,
	useUpdateSalesOderV2Mutation,
	useGetSalesOrderDetailV2Query,
	useLazyGetSalesOrderInvoicePDFQuery,
	useGetSalesOrderReportV2Query,
	useCompleteSalesOrderV2Mutation,
} = api.injectEndpoints({
	endpoints: (build) => ({
		createSalesOder: build.mutation<void, Record<string, any>>({
			query: (data) => {
				return {
					url,
					method: "POST",
					body: data,
				};
			},
			invalidatesTags: (result, error) => {
				return error ? [] : [{ type: "sales-order", id: "LIST" }];
			},
		}),
		updateSalesOder: build.mutation<void, Record<string, any>>({
			query: (body) => {
				const { id } = body;
				return {
					url: `${url}/${id}`,
					method: "PUT",
					body,
				};
			},
			invalidatesTags: (result, error, { id }) => {
				return error
					? []
					: [
							{ type: "sales-order", id },
							{ type: "sales-order", id: "LIST" },
							{ type: "sales-order-detail", id },
						];
			},
		}),
		getSalesOrders: build.query<ResponsePaginated<SalesOrder>, Record<string, any>>({
			query: (query) => `${url}?${qs.stringify(query)}`,
			providesTags: (result) => providesList(result?.items, "sales-order"),
		}),
		getOneSalesOrder: build.query<SalesOrder, number>({
			query: (salesOrderId) => `${url}/${salesOrderId}`,
			providesTags: (result, _, salesOrderId) => [{ type: "sales-order", id: salesOrderId }],
		}),
		getSalesOrderDetail: build.query<SalesOrderDetail[], number>({
			query: (salesOrderId) => `sales-order-detail/${salesOrderId}`,
			providesTags: (result, _, salesOrderId) => [{ type: "sales-order-detail", id: salesOrderId }],
		}),
		deleteSalesOrder: build.mutation<void, number>({
			query: (id) => {
				return {
					url: `${url}/${id}`,
					method: "DELETE",
				};
			},
			invalidatesTags: (_, error, id) => (error ? [] : [{ type: "sales-order", id }]),
		}),
		cancelSalesOrder: build.mutation<void, number>({
			query: (id) => {
				return {
					url: `${url}/cancel/${id}`,
					method: "PUT",
				};
			},
			invalidatesTags: (_, error, id) => (error ? [] : [{ type: "sales-order", id }]),
		}),
		getSalesOrderInvoice: build.query<{ UrlPDF: string }, number>({
			query: (salesOrderId) => `${url}/invoice?salesOrderId=${salesOrderId}`,
		}),
		createSalesOderV2: build.mutation<void, Record<string, any>>({
			query: (data) => {
				return {
					url: `${url}/v2`,
					method: "POST",
					body: data,
				};
			},
			invalidatesTags: (result, error) => {
				return error ? [] : [{ type: "sales-order", id: "LIST" }];
			},
		}),
		updateSalesOderV2: build.mutation<void, Record<string, any>>({
			query: (body) => {
				const { id } = body;
				return {
					url: `${url}/v2/${id}`,
					method: "PUT",
					body,
				};
			},
			invalidatesTags: (result, error, { id }) => {
				return error
					? []
					: [
							{ type: "sales-order", id },
							{ type: "sales-order", id: "LIST" },
							{ type: "sales-order-detail", id },
						];
			},
		}),
		getOneSalesOrderV2: build.query<SalesOrderV2, number>({
			query: (salesOrderId) => `${url}/v2/${salesOrderId}`,
			providesTags: (result, _, salesOrderId) => [{ type: "sales-order", id: salesOrderId }],
		}),
		getSalesOrderDetailV2: build.query<SalesOrderDetailV2[], number>({
			query: (salesOrderId) => `sales-order-detail/v2/${salesOrderId}`,
			providesTags: (result, _, salesOrderId) => [{ type: "sales-order-detail", id: salesOrderId }],
		}),
		getSalesOrderInvoicePDF: build.query<{ data: string }, string>({
			query: (fkey) => `sales-order/invoice/${fkey}`,
		}),
		getSalesOrderReportV2: build.query<ResponsePaginated<SalesOrderReportV2Response>, Record<string, any>>({
			query: (query) => `sales-order/v2/report?${qs.stringify(query)}`,
			providesTags: (result) => providesList(result?.items, "sales-order-report"),
		}),
		completeSalesOrderV2: build.mutation<void, number>({
			query: (id) => {
				return {
					url: `${url}/v2/complete/${id}`,
					method: "PUT",
				};
			},
			invalidatesTags: (result, error, id) => {
				return error
					? []
					: [
							{ type: "sales-order-report", id },
							{ type: "sales-order-report", id: "LIST" },
						];
			},
		}),
	}),
});
